import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatDivider } from '@angular/material/divider'
import { ROUTES } from '../../../constants/routes'
import { ActivatedRoute } from '@angular/router'
import { RouteService } from '../../../services/route.service'
import { ProductRegulation } from '../../../types/products'
import { MatToolbar } from '@angular/material/toolbar'
import { MatExpansionModule } from '@angular/material/expansion'
import { WindowService } from '../../../services/window.service'
import { ProductService } from '../../../services/product.service'
import { ErrorService } from '../../../services/error.service'
import { SpinnerService } from '../../../services/spinner.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ApiError } from '../../../types/apiError'
import { Dialog } from '@angular/cdk/dialog'
import { DialogService } from '../../../services/dialog.service'
import { RegulationTableComponent } from './regulation-table/regulation-table.component'
import { TranslationService } from '../../../services/translation.service'

@Component({
  selector: 'app-product-regulator-info',
  imports: [
    MatDivider,
    MatToolbar,
    MatExpansionModule,
    RegulationTableComponent,
  ],
  templateUrl: './product-regulator-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-regulator-info.component.scss',
})
export class ProductRegulatorInfoComponent implements OnInit {
  routeService = inject(RouteService)
  private route = inject(ActivatedRoute)
  windowService = inject(WindowService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  translationService = inject(TranslationService)

  productRegulation: ProductRegulation | undefined

  isMobileView: boolean = false

  /**
   * Below are mappings used to create the regulation tables. For future additions, it should be
   * [propertyName, Human Readable Column Name]. The property name is used as the column definition in
   * the regulation-table component
   */
  cdslMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['dsl', this.translationService.getDslText()],
    ['ndsl', this.translationService.getNdslText()],
    ['ricl', this.translationService.getRicLText()],
    ['tsl', this.translationService.getTslText()],
    ['maxPercentage', this.translationService.getMaxPercentageText()],
    ['triggerQtyInKg', this.translationService.getTriggerQtyInKgText()],
    ['nextSchedule', this.translationService.getNextScheduleText()],
    [
      'scheduleViPrecursor',
      this.translationService.getScheduleViPrecursorText(),
    ],
    ['nsnNumber', this.translationService.getNsnNumberText()],
  ])

  cepaMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['snac', this.translationService.getSnacText()],
    [
      'ministerialCondition',
      this.translationService.getMinisterialConditionText(),
    ],
    ['npri', this.translationService.getNpriText()],
    ['e2', this.translationService.getE2Text()],
  ])

  cbsaMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['exportControl', this.translationService.getExportControlText()],
    [
      'exportControlPermit',
      this.translationService.getExportControlPermitText(),
    ],
    ['importControlList', this.translationService.getImportControlListText()],
    [
      'importControlListPermit',
      this.translationService.getImportControlListPermitText(),
    ],
  ])

  usMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['tscaInv', this.translationService.getTscaInvText()],
    [
      'carcinogenicityIARC',
      this.translationService.getCarcinogenicityIARCText(),
    ],
    [
      'carcinogenicityOSHA',
      this.translationService.getCarcinogenicityOSHAText(),
    ],
    ['carcinogenicityNTP', this.translationService.getCarcinogenicityNTPText()],
    ['tsca4', this.translationService.getTsca4Text()],
    ['snur', this.translationService.getSnurText()],
    ['tsca5E', this.translationService.getTsca5EText()],
    ['pmnNumber', this.translationService.getPmnNumberText()],
    [
      'noticeOfCommencement',
      this.translationService.getNoticeOfCommencementText(),
    ],
    ['tsca6', this.translationService.getTsca6Text()],
    ['cdr', this.translationService.getCdrText()],
    ['cercla', this.translationService.getCerclaText()],
    ['sara302', this.translationService.getSara302Text()],
    ['sara311312', this.translationService.getSara311312Text()],
    ['sara313', this.translationService.getSara313Text()],
    ['rcra', this.translationService.getRcraText()],
    ['prop65', this.translationService.getProp65Text()],
    ['control', this.translationService.getControlText()],
    ['tri', this.translationService.getTriText()],
  ])

  dotTdgMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['unNumber', this.translationService.getUnNumberText()],
    ['properShippingName', this.translationService.getProperShippingNameText()],
    ['classification', this.translationService.getClassificationText()],
    ['packingGroup', this.translationService.getPackingGroupText()],
    ['ltdQtyGround', this.translationService.getLtdQtyGroundText()],
    ['ltdQtyAir', this.translationService.getLtdQtyAirText()],
    ['ltdQtyCargo', this.translationService.getLtdQtyCargoText()],
    ['marinePollutant', this.translationService.getMarinePollutantText()],
    ['erap', this.translationService.getErapText()],
    ['erapNumber', this.translationService.getErapNumberText()],
    ['erapExpiryDate', this.translationService.getErapExpiryDateText()],
    ['tpOrCgsbStd', this.translationService.getTpOrCgsbStdText()],
    ['temperatureControl', this.translationService.getTemperatureControlText()],
    ['flashPointC', this.translationService.getFlashPointCText()],
    ['freezeProtect', this.translationService.getFreezeProtectText()],
  ])

  whmisEachMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['whmisClasses', this.translationService.getWhmisClassesText()],
    ['supplierSdsNumber', this.translationService.getSupplierSdsNumberText()],
    ['supplierSdsDate', this.translationService.getSupplierSdsDateText()],
  ])

  precursorMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['drugPrecursor', this.translationService.getDrugPrecursorText()],
    ['deaNumber', this.translationService.getDeaNumberText()],
    [
      'restrictedComponent',
      this.translationService.getRestrictedComponentText(),
    ],
    ['securityPlan', this.translationService.getSecurityPlanText()],
    ['cwc', this.translationService.getCwcText()],
  ])

  wasteMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['wasteCode', this.translationService.getWasteCodeText()],
  ])

  whmisClassMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['phClass', this.translationService.getPhClassText()],
    ['phSdsNumber', this.translationService.getPhSdsNumberText()],
    ['phSdsDate', this.translationService.getPhSdsDateText()],
    ['labelNumber', this.translationService.getLabelNumberText()],
    ['labelDate', this.translationService.getLabelDateText()],
  ])

  hsMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['importerOfRecord', this.translationService.getImporterOfRecordText()],
    ['hsCodeCanada', this.translationService.getHsCodeCanadaText()],
    ['dutyRateCanada', this.translationService.getDutyRateCanadaText()],
    ['usaHsCode', this.translationService.getUsaHsCodeText()],
    ['dutyRateUsa', this.translationService.getDutyRateUsaText()],
    ['cfiaAirsCode', this.translationService.getCfiaAirsCodeText()],
    ['tariff', this.translationService.getTariffText()],
    ['countryOfOrigin', this.translationService.getCountryOfOriginText()],
    ['certificateOnFile', this.translationService.getCertificateOnFileText()],
  ])

  commentsMapping = new Map<string, string>([
    ['casNumber', this.translationService.getCasHashtagText()],
    ['name', this.translationService.getNameText()],
    ['comments', this.translationService.getCommentsText()],
  ])

  async ngOnInit() {
    try {
      this.windowService.isMobileView$.subscribe((isMobileView) => {
        this.isMobileView = isMobileView
      })
      this.spinnerService.startSpinner()
      const productId = this.route.parent?.snapshot.params['id']
      this.productRegulation = await firstValueFrom(
        this.productService.getRegulation(productId),
      )

      if (!this.productRegulation) {
        throw Error('Product regulation must be provided')
      }

      const currentRoute = this.routeService.getProductViewRoutes(
        this.productRegulation.name,
        ROUTES.PRODUCT_REGULATIONS.path,
        productId,
      )
      this.routeService.setRoute(currentRoute)

      this.productService.currentProductData.set({
        id: this.productRegulation.id,
        name: this.productRegulation.name,
        brand: this.productRegulation.brand,
      })
      this.routeService.setRoute(currentRoute)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Returns string for a nullable boolean value
   * @param {boolean} bool The boolean value
   * @returns {string} The formatted string
   */
  formatNullableBool(bool: boolean | null): string {
    if (bool != null) {
      if (bool === true) {
        return this.translationService.getYText()
      } else {
        return this.translationService.getNText()
      }
    }
    return this.translationService.getNaText()
  }

  /**
   * Gets the column names that should be used for a regulation table
   */
  getColumnsForTable(columnNameMap: Map<string, string>) {
    return Array.from(columnNameMap.keys())
  }
}
